
import {effect, signal} from "@e280/strata"
import {disposer, ev, nap, subby} from "@e280/stz"

import {User} from "./user.js"
import {consts} from "../../consts.js"
import {AuthLike} from "./types/auth-like.js"
import {openPopup} from "./parts/open-popup.js"
import {AuthOptions} from "./types/auth-options.js"
import {SessionOptions} from "./types/session-options.js"
import {isSessionValid} from "./parts/is-session-valid.js"
import {sessionPetitions} from "./parts/session-petitions.js"
import {askForDelegates} from "./parts/ask-for-delegates.js"
import {defaultAuthOptions} from "./parts/default-auth-options.js"

/** auth facility for logging in and out. */
export class Auth implements AuthLike {
	dispose

	#sub = subby<[User | null]>()
	#options
	#$user = signal<User | null>(null)

	constructor(options: Partial<AuthOptions> = {}) {
		const {dispose, schedule} = disposer()
		this.dispose = dispose

		this.#options = defaultAuthOptions(options)

		schedule(
			ev(this.#options.broadcastChannel, {
				message: () => nap(consts.broadcastReloadDelay).then(() => this.remember())
			})
		)

		schedule(
			effect(
				() => this.#$user(),
				user => this.#sub.publish(user),
			)
		)
	}

	get on() {
		return this.#sub.on
	}

	/** validate and return the current session, otherwise return null. */
	get user() {
		const user = this.#$user()
		return (user && isSessionValid(user.session))
			? user
			: null
	}

	/** remember a previous login from persistent storage. */
	async remember() {
		const session = await this.#options.sessionCubby.get()
		const user = session
			? new User(session)
			: null
		this.#$user(user)
		return this.user
	}

	/** log out immediately. */
	async logout() {
		await this.#options.sessionCubby.set(undefined)
		this.#$user(null)
		this.#options.broadcastChannel.postMessage(true)
	}

	/** ask for a new login from the delegator */
	async loginViaPopup(options: Partial<SessionOptions> = {}) {
		const petitions = sessionPetitions(options)
		const delegatorOrigin = new URL(this.#options.delegatorUrl, window.location.href).origin

		const popup = openPopup("auth", this.#options.delegatorUrl)
		const [auth, crypt] = await askForDelegates(popup, delegatorOrigin, petitions)

		const user = new User({auth, crypt})
		await this.#options.sessionCubby.set(user.session)
		this.#$user(user)
		this.#options.broadcastChannel.postMessage(true)
		return this.user
	}
}

